import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { PRICE_STATS } from '../sbSharedRefineMenuHelpers.ts';
import { CURRENCY } from '../sbSharedRefineMenuConstants.ts';
import { areFiltersApplied } from '../../helpers/appliedFilters/areFiltersApplied.ts';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';

// components
import { SbSharedRefineMenuInputRange } from './SbSharedRefineMenuInputRange.tsx';

import { type SbSharedRefineMenuInputRangeCurrency_itemSearch$key } from './__generated__/SbSharedRefineMenuInputRangeCurrency_itemSearch.graphql.ts';
import { type SbSharedRefineMenuInputRangeCurrency_filters$key } from './__generated__/SbSharedRefineMenuInputRangeCurrency_filters.graphql.ts';

const messages = defineMessages({
    labelTo: {
        id: 'sb.SbSharedRefineMenuFilterPrice.maximum',
        defaultMessage: '{currency} max',
    },
    labelFrom: {
        id: 'sb.SbSharedRefineMenuFilterPrice.minimum',
        defaultMessage: '{currency} min',
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuInputRangeCurrency_itemSearch on ItemSearchQueryConnection {
        ...SbSharedRefineMenuInputRange_itemSearch
        ...areFiltersApplied_itemSearch
    }
`;
const filterFragment = graphql`
    fragment SbSharedRefineMenuInputRangeCurrency_filters on SearchBrowseFilter {
        values {
            ...SbSharedRefineMenuInputRange_filterValue
        }
    }
`;

type Props = {
    itemSearch: SbSharedRefineMenuInputRangeCurrency_itemSearch$key;
    filter: SbSharedRefineMenuInputRangeCurrency_filters$key;
    applyOnBlur?: boolean;
    filterName: string;
    showInvalidRangeErrorMessage?: boolean;
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuInputRangeCurrency: FC<Props> = ({
    itemSearch: itemSearchRef,
    filter: filterRef,
    applyOnBlur,
    filterName,
    showInvalidRangeErrorMessage,
    trackingOptions,
}) => {
    const intl = useIntl();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filter = useFragment(filterFragment, filterRef);
    // The price values should be prefilled if they are coming from appliedFilters
    const showAnimatedPlaceholder = !areFiltersApplied(itemSearch, PRICE_STATS);
    const { currency } = useCurrency();

    return (
        <SbSharedRefineMenuInputRange
            itemSearch={itemSearch}
            filterValue={filter?.values?.[0]}
            applyOnBlur={applyOnBlur}
            filterName={filterName}
            labelFrom={intl.formatMessage(messages.labelFrom, { currency })}
            labelTo={intl.formatMessage(messages.labelTo, { currency })}
            statKey={currency}
            additionalFilter={CURRENCY}
            showAnimatedPlaceholder={showAnimatedPlaceholder}
            showInvalidRangeErrorMessage={showInvalidRangeErrorMessage}
            trackCustomRange
            trackingOptions={trackingOptions}
        />
    );
};
